import type { AutomationSession, AutomationSessionStatus } from '../types'
import { automationEventBus } from '../events'

/**
 * SessionManager — create / renew / close / expire sessions.
 */
export class SessionManager {
  private readonly sessions = new Map<string, AutomationSession>()
  private sessionTtlMs: number

  constructor(sessionTtlMs = 30 * 60_000) {
    this.sessionTtlMs = sessionTtlMs
  }

  setTtl(ms: number): void {
    this.sessionTtlMs = Math.max(1000, ms)
  }

  create(supplierId: string): AutomationSession {
    this.purgeExpired()
    const now = Date.now()
    const session: AutomationSession = {
      id: crypto.randomUUID(),
      supplierId,
      status: 'ACTIVE',
      createdAt: new Date(now).toISOString(),
      expiresAt: new Date(now + this.sessionTtlMs).toISOString(),
      lastActivity: new Date(now).toISOString(),
    }
    this.sessions.set(session.id, session)
    return session
  }

  renew(sessionId: string): AutomationSession {
    const session = this.get(sessionId)
    if (!session) throw new Error('Sessão não encontrada')
    if (session.status !== 'ACTIVE' || this.isExpired(session)) {
      this.expire(sessionId)
      throw new Error('Sessão expirada')
    }
    const now = Date.now()
    const next: AutomationSession = {
      ...session,
      lastActivity: new Date(now).toISOString(),
      expiresAt: new Date(now + this.sessionTtlMs).toISOString(),
      status: 'ACTIVE',
    }
    this.sessions.set(sessionId, next)
    return next
  }

  touch(sessionId: string): AutomationSession {
    return this.renew(sessionId)
  }

  close(sessionId: string): AutomationSession | null {
    const session = this.get(sessionId)
    if (!session) return null
    const next: AutomationSession = { ...session, status: 'CLOSED', lastActivity: new Date().toISOString() }
    this.sessions.set(sessionId, next)
    return next
  }

  expire(sessionId: string): AutomationSession | null {
    const session = this.get(sessionId)
    if (!session) return null
    const next: AutomationSession = {
      ...session,
      status: 'EXPIRED',
      lastActivity: new Date().toISOString(),
    }
    this.sessions.set(sessionId, next)
    automationEventBus.emit('SessionExpired', `Sessão expirada: ${session.supplierId}`, {
      supplierId: session.supplierId,
      sessionId,
    })
    return next
  }

  get(sessionId: string): AutomationSession | null {
    return this.sessions.get(sessionId) ?? null
  }

  list(status?: AutomationSessionStatus): AutomationSession[] {
    this.purgeExpired()
    const all = [...this.sessions.values()]
    if (!status) return all
    return all.filter((s) => s.status === status)
  }

  getActiveForSupplier(supplierId: string): AutomationSession | null {
    this.purgeExpired()
    return (
      [...this.sessions.values()].find(
        (s) => s.supplierId === supplierId && s.status === 'ACTIVE' && !this.isExpired(s),
      ) ?? null
    )
  }

  purgeExpired(): void {
    for (const session of this.sessions.values()) {
      if (session.status === 'ACTIVE' && this.isExpired(session)) {
        this.expire(session.id)
      }
    }
  }

  reset(): void {
    this.sessions.clear()
  }

  private isExpired(session: AutomationSession): boolean {
    return new Date(session.expiresAt).getTime() <= Date.now()
  }
}

export const sessionManager = new SessionManager()
